import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, UploadCloud, X, Zap } from "lucide-react";
import { api, fmtErr } from "../../lib/api";

const MIN = { address_street: "", unit_number: "", city: "Vancouver", neighborhood: "", beds: "1", baths: "1", monthly_rent: "", company_name: "", date_available: "", description: "" };

export async function fileToDataUrl(file, maxW = 1000) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.78));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function QuickAdd({ onClose, onSaved }) {
  const [form, setForm] = useState(MIN);
  const [companies, setCompanies] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = async (files, target) => {
    const urls = [];
    for (const f of Array.from(files).slice(0, 8)) {
      if (!f.type.startsWith("image/")) continue;
      try { urls.push(await fileToDataUrl(f)); } catch { /* skip unreadable file */ }
    }
    if (target === "photos") setPhotos((p) => [...p, ...urls].slice(0, 8));
    else setPlans((p) => [...p, ...urls].slice(0, 4));
  };

  const parsePaste = async () => {
    if (!paste.trim()) return;
    setParsing(true);
    try {
      const r = await api.post("/admin/listings/parse-text", { text: paste });
      const d = r.data;
      setForm((f) => ({
        ...f,
        address_street: d.address_street ?? f.address_street,
        unit_number: d.unit_number ?? f.unit_number,
        city: d.city ?? f.city,
        neighborhood: d.neighborhood ?? f.neighborhood,
        beds: d.beds != null ? String(d.beds) : f.beds,
        baths: d.baths != null ? String(d.baths) : f.baths,
        monthly_rent: d.monthly_rent != null ? String(d.monthly_rent) : f.monthly_rent,
        company_name: d.company_name ?? f.company_name,
        date_available: d.date_available ?? f.date_available,
        description: d.description ?? f.description,
      }));
      toast.success("Form pre-filled — review before saving");
    } catch (e) { toast.error(fmtErr(e)); }
    setParsing(false);
  };

  const save = async () => {
    if (!form.address_street || form.beds === "" || form.baths === "" || !form.monthly_rent || !form.company_name || !form.date_available) {
      return toast.error("Required: address, beds, baths, monthly rent, managing company, date available.");
    }
    setBusy(true);
    const comp = companies.find((c) => c.name === form.company_name);
    const payload = {
      ...form,
      beds: Number(form.beds), baths: Number(form.baths), monthly_rent: Number(form.monthly_rent),
      status: "active", source: "manual", source_name: "Direct",
      images: photos.map((u) => ({ url: u, alt: `${form.beds === "0" ? "Studio" : `${form.beds}-bedroom`} rental in ${form.neighborhood || form.city}` })),
      floor_plan_images: plans,
      ...(comp ? { company_id: comp.company_id, company_type: comp.type, landlord_contact_email: comp.email, landlord_contact_phone: comp.phone, landlord_contact_name: comp.primary_contact_name, referral_status: comp.referral_status } : {}),
    };
    try {
      const r = await api.post("/admin/listings", payload);
      if (r.data.action === "added") toast.success("Listing added");
      else toast.warning("A similar address/unit already existed — merged into the existing record.");
      setSaved(true);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const resetForAnother = () => {
    setForm(MIN);
    setPhotos([]);
    setPlans([]);
    setPaste("");
    setSaved(false);
  };

  const inputCls = "w-full rounded-xl border border-input bg-background px-3.5 py-2 text-sm";

  if (saved) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Listing saved">
        <div data-testid="qa-saved" className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <span className="inline-flex w-14 h-14 rounded-full bg-teal-50 dark:bg-teal-900/40 text-primary items-center justify-center"><Zap className="w-7 h-7" /></span>
          <h2 className="mt-4 font-heading font-bold text-2xl">Listing saved</h2>
          <p className="mt-2 text-sm text-muted-foreground">It's live in the inventory. Add the next one while you're in the flow.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button data-testid="qa-add-another" onClick={resetForAnother}
              className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold">Add another listing</button>
            <button data-testid="qa-done" onClick={onSaved}
              className="rounded-full border border-border px-6 py-2.5 text-sm font-semibold">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Quick add listing">
      <div data-testid="quick-add-modal" className="w-full max-w-3xl rounded-2xl border border-border bg-card p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-xl flex items-center gap-2"><Zap className="w-5 h-5 text-accent" /> Quick Add Listing</h2>
          <button data-testid="qa-close" onClick={onClose} aria-label="Close" className="p-2 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        {/* Paste to fill */}
        <div className="mt-4 rounded-xl border border-dashed border-primary/40 bg-teal-50/50 dark:bg-teal-900/20 p-4">
          <label className="block text-xs font-semibold mb-1.5" htmlFor="qa-paste">Paste to fill — paste listing text you copied, AI pre-fills the form</label>
          <textarea id="qa-paste" data-testid="qa-paste" value={paste} onChange={(e) => setPaste(e.target.value)} rows={3}
            placeholder="e.g. Bright 2 bed 1 bath at 2210 W 4th Ave, Kitsilano. $2,850/mo, 880 sqft, available Aug 1. Managed by Nestwell Properties…"
            className="w-full rounded-xl border border-input bg-background px-3.5 py-2 text-sm" />
          <button data-testid="qa-parse" onClick={parsePaste} disabled={parsing || !paste.trim()}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold disabled:opacity-40">
            {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Parse & pre-fill
          </button>
        </div>

        {/* Photos drop zone */}
        <div
          data-testid="qa-photos-drop"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files, "photos"); }}
          className={`mt-4 rounded-xl border-2 border-dashed p-5 text-center transition-colors ${dragOver ? "border-primary bg-teal-50/60 dark:bg-teal-900/20" : "border-border"}`}>
          <UploadCloud className="w-6 h-6 mx-auto text-muted-foreground" />
          <p className="mt-1 text-sm font-medium">Drag & drop photos here</p>
          <p className="text-xs text-muted-foreground">or</p>
          <label className="mt-1 inline-block cursor-pointer text-sm font-semibold text-primary hover:underline">
            Browse photos
            <input data-testid="qa-photos-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files, "photos")} />
          </label>
          <label className="ml-4 inline-block cursor-pointer text-sm font-semibold text-primary hover:underline">
            Add floor plans
            <input data-testid="qa-plans-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files, "plans")} />
          </label>
          {(photos.length > 0 || plans.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              {photos.map((u, i) => (
                <span key={`p${i}`} className="relative">
                  <img src={u} alt={`Upload ${i + 1}`} className="w-16 h-12 object-cover rounded-lg border border-border" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label="Remove photo" className="absolute -top-1.5 -right-1.5 bg-card border border-border rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {plans.map((u, i) => (
                <span key={`f${i}`} className="relative">
                  <img src={u} alt={`Floor plan ${i + 1}`} className="w-16 h-12 object-cover rounded-lg border-2 border-primary" />
                  <button onClick={() => setPlans(plans.filter((_, j) => j !== i))} aria-label="Remove floor plan" className="absolute -top-1.5 -right-1.5 bg-card border border-border rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Minimal fields */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="col-span-2"><label className="block text-xs font-semibold mb-1" htmlFor="qa-address">Street address *</label>
            <input id="qa-address" data-testid="qa-address" value={form.address_street} onChange={(e) => set("address_street", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-unit">Unit #</label>
            <input id="qa-unit" data-testid="qa-unit" value={form.unit_number} onChange={(e) => set("unit_number", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-neighborhood">Neighbourhood</label>
            <input id="qa-neighborhood" data-testid="qa-neighborhood" value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-city">City</label>
            <input id="qa-city" data-testid="qa-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-beds">Beds * (0 = studio)</label>
            <input id="qa-beds" data-testid="qa-beds" type="number" min="0" value={form.beds} onChange={(e) => set("beds", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-baths">Baths *</label>
            <input id="qa-baths" data-testid="qa-baths" type="number" min="0.5" step="0.5" value={form.baths} onChange={(e) => set("baths", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-rent">Monthly rent (CAD) *</label>
            <input id="qa-rent" data-testid="qa-rent" type="number" min="0" value={form.monthly_rent} onChange={(e) => set("monthly_rent", e.target.value)} className={inputCls} /></div>
          <div><label className="block text-xs font-semibold mb-1" htmlFor="qa-available">Date available *</label>
            <input id="qa-available" data-testid="qa-available" type="date" value={form.date_available} onChange={(e) => set("date_available", e.target.value)} className={inputCls} /></div>
          <div className="col-span-2"><label className="block text-xs font-semibold mb-1" htmlFor="qa-company">Managing company / landlord *</label>
            <input id="qa-company" data-testid="qa-company" list="qa-companies" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} placeholder="Start typing…" />
            <datalist id="qa-companies">{companies.map((c) => <option key={c.company_id} value={c.name} />)}</datalist></div>
          <div className="col-span-2 sm:col-span-3"><label className="block text-xs font-semibold mb-1" htmlFor="qa-description">Description</label>
            <textarea id="qa-description" data-testid="qa-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls} /></div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button data-testid="qa-cancel" onClick={onClose} className="rounded-full border border-border px-5 py-2 text-sm font-medium">Cancel</button>
          <button data-testid="qa-save" onClick={save} disabled={busy}
            className="rounded-full bg-primary text-primary-foreground px-6 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save listing
          </button>
        </div>
      </div>
    </div>
  );
}
